/**
 * Optimal-team constraints UI for the Apex Team page.
 *
 * - TeamFiltersPanel: popover with min/max bounds on expected points,
 *   expected price change, and total cost. Applied inside the optimizer so
 *   results are complete, not a post-filter of the unfiltered top list.
 * - AvoidSection: "avoid list" of drivers/constructors excluded from every
 *   generated team (picked via the existing SelectionModal).
 */

import { Ban, Filter, Plus, X } from "lucide-react";
import { useState } from "react";
import { m } from "@/paraglide/messages";
import type { PredictedConstructor, PredictedDriver } from "../lib/algorithms";
import { getTeamColor } from "../lib/team-colors";
import type { TeamFilters } from "../lib/team-filters";
import { countActiveFilters } from "../lib/team-filters";
import { type SelectionItem, SelectionModal } from "./SelectionModal";

// ============================================================================
// Filters popover
// ============================================================================

interface TeamFiltersPanelProps {
  filters: TeamFilters;
  onChange: (filters: TeamFilters) => void;
  isPredictionMode: boolean;
}

type DraftFilters = Record<keyof TeamFilters, string>;

function toDraft(filters: TeamFilters): DraftFilters {
  const str = (v: number | undefined) => (v === undefined ? "" : String(v));
  return {
    minPoints: str(filters.minPoints),
    maxPoints: str(filters.maxPoints),
    minBudgetChange: str(filters.minBudgetChange),
    maxBudgetChange: str(filters.maxBudgetChange),
    minCost: str(filters.minCost),
    maxCost: str(filters.maxCost),
  };
}

function fromDraft(draft: DraftFilters): TeamFilters {
  const num = (s: string): number | undefined => {
    if (s.trim() === "") return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    minPoints: num(draft.minPoints),
    maxPoints: num(draft.maxPoints),
    minBudgetChange: num(draft.minBudgetChange),
    maxBudgetChange: num(draft.maxBudgetChange),
    minCost: num(draft.minCost),
    maxCost: num(draft.maxCost),
  };
}

export function TeamFiltersPanel({
  filters,
  onChange,
  isPredictionMode,
}: TeamFiltersPanelProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftFilters>(() => toDraft(filters));
  const activeCount = countActiveFilters(filters);

  const openPanel = () => {
    setDraft(toDraft(filters));
    setOpen(true);
  };

  const apply = () => {
    onChange(fromDraft(draft));
    setOpen(false);
  };

  const clearAll = () => {
    onChange({});
    setOpen(false);
  };

  const rows: { key: "Points" | "BudgetChange" | "Cost"; label: string }[] = [
    {
      key: "Points",
      label: isPredictionMode
        ? m.f1_apex_filter_points_predicted()
        : m.f1_apex_filter_points(),
    },
    { key: "BudgetChange", label: m.f1_apex_filter_price_change() },
    { key: "Cost", label: m.f1_apex_filter_cost() },
  ];

  const inputClass =
    "h-8 w-full rounded-md border border-border bg-background px-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors min-h-[32px] ${
          activeCount > 0
            ? "border-primary/50 bg-primary/10 text-primary"
            : "border-border bg-muted/40 text-muted-foreground hover:text-foreground"
        }`}
      >
        <Filter className="h-3.5 w-3.5" aria-hidden="true" />
        {m.f1_apex_filters()}
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground font-mono text-[10px]">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
            aria-label={m.f1_apex_filters_close()}
          />
          <div className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] p-4 rounded-xl bg-card border border-border shadow-xl z-50">
            <p className="font-semibold text-sm text-foreground mb-3">
              {m.f1_apex_filters_title()}
            </p>
            <div className="space-y-3">
              {rows.map(({ key, label }) => (
                <div key={key}>
                  <span className="block text-xs text-muted-foreground mb-1">
                    {label}
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      value={draft[`min${key}`]}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [`min${key}`]: e.target.value,
                        }))
                      }
                      placeholder={m.f1_apex_filter_min()}
                      aria-label={`${label} — ${m.f1_apex_filter_min()}`}
                      className={inputClass}
                    />
                    <input
                      type="number"
                      inputMode="decimal"
                      value={draft[`max${key}`]}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [`max${key}`]: e.target.value,
                        }))
                      }
                      placeholder={m.f1_apex_filter_max()}
                      aria-label={`${label} — ${m.f1_apex_filter_max()}`}
                      className={inputClass}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 mt-4">
              <button
                type="button"
                onClick={clearAll}
                className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {m.f1_apex_filters_clear()}
              </button>
              <button
                type="button"
                onClick={apply}
                className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:brightness-110 transition-all"
              >
                {m.f1_apex_filters_apply()}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Avoid list
// ============================================================================

interface AvoidSectionProps {
  drivers: PredictedDriver[];
  constructors: PredictedConstructor[];
  avoidDriverIds: string[];
  avoidConstructorIds: string[];
  onAvoidDriversChange: (ids: string[]) => void;
  onAvoidConstructorsChange: (ids: string[]) => void;
  /** Ids currently locked into team slots — cannot be avoided. */
  selectedDriverIds: string[];
  selectedConstructorIds: string[];
  isPredictionMode: boolean;
}

export function AvoidSection({
  drivers,
  constructors,
  avoidDriverIds,
  avoidConstructorIds,
  onAvoidDriversChange,
  onAvoidConstructorsChange,
  selectedDriverIds,
  selectedConstructorIds,
  isPredictionMode,
}: AvoidSectionProps) {
  const [modal, setModal] = useState<"closed" | "driver" | "constructor">(
    "closed",
  );

  const driverItems: SelectionItem[] = drivers
    .filter(
      (d) =>
        !avoidDriverIds.includes(d.id) && !selectedDriverIds.includes(d.id),
    )
    .map((d) => ({
      id: d.id,
      name: d.fullName,
      price: d.currentPrice,
      priceChange: d.predictedPriceChange,
      racePoints: d.predictedRacePoints,
      teamColor: getTeamColor(d.constructorSlug),
      teamName: d.constructorName,
    }));

  const constructorItems: SelectionItem[] = constructors
    .filter(
      (c) =>
        !avoidConstructorIds.includes(c.id) &&
        !selectedConstructorIds.includes(c.id),
    )
    .map((c) => ({
      id: c.id,
      name: c.name,
      price: c.currentPrice,
      priceChange: c.predictedPriceChange,
      racePoints: c.predictedRacePoints,
      teamColor: getTeamColor(c.slug),
    }));

  const avoidedChips = [
    ...avoidDriverIds.map((id) => {
      const d = drivers.find((x) => x.id === id);
      return d
        ? {
            id,
            kind: "driver" as const,
            name: d.fullName,
            color: getTeamColor(d.constructorSlug),
          }
        : null;
    }),
    ...avoidConstructorIds.map((id) => {
      const c = constructors.find((x) => x.id === id);
      return c
        ? {
            id,
            kind: "constructor" as const,
            name: c.name,
            color: getTeamColor(c.slug),
          }
        : null;
    }),
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  const removeAvoided = (id: string, kind: "driver" | "constructor") => {
    if (kind === "driver")
      onAvoidDriversChange(avoidDriverIds.filter((x) => x !== id));
    else onAvoidConstructorsChange(avoidConstructorIds.filter((x) => x !== id));
  };

  return (
    <div className="p-3 rounded-xl border border-border bg-card/50">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-2">
          <Ban className="h-4 w-4 text-destructive/80" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground">
            {m.f1_apex_avoid_title()}
          </span>
          <span className="hidden sm:inline text-xs text-muted-foreground">
            {m.f1_apex_avoid_desc()}
          </span>
        </div>

        {avoidedChips.map((chip) => (
          <span
            key={chip.id}
            className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-muted/50 border border-border text-xs"
          >
            <span
              className="w-1 h-4 rounded-full shrink-0"
              style={{ backgroundColor: chip.color }}
              aria-hidden="true"
            />
            <span className="line-through decoration-destructive/60 text-foreground">
              {chip.name}
            </span>
            <button
              type="button"
              onClick={() => removeAvoided(chip.id, chip.kind)}
              aria-label={m.f1_apex_avoid_remove({ name: chip.name })}
              className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}

        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => setModal("driver")}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-muted/40 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            {m.f1_apex_avoid_driver()}
          </button>
          <button
            type="button"
            onClick={() => setModal("constructor")}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-muted/40 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            {m.f1_apex_avoid_constructor()}
          </button>
        </div>
      </div>

      <SelectionModal
        isOpen={modal === "driver"}
        onClose={() => setModal("closed")}
        title={m.f1_apex_avoid_pick_driver()}
        items={driverItems}
        onSelect={(id) => {
          if (id) onAvoidDriversChange([...avoidDriverIds, id]);
          setModal("closed");
        }}
        currentSelectedId={null}
        selectedElsewhereMap={new Map()}
        remainingBudget={Number.POSITIVE_INFINITY}
        isPredictionMode={isPredictionMode}
      />
      <SelectionModal
        isOpen={modal === "constructor"}
        onClose={() => setModal("closed")}
        title={m.f1_apex_avoid_pick_constructor()}
        items={constructorItems}
        onSelect={(id) => {
          if (id) onAvoidConstructorsChange([...avoidConstructorIds, id]);
          setModal("closed");
        }}
        currentSelectedId={null}
        selectedElsewhereMap={new Map()}
        remainingBudget={Number.POSITIVE_INFINITY}
        isPredictionMode={isPredictionMode}
      />
    </div>
  );
}
